"use server";

import { apiClient, ApiClientError } from "@/lib/api/client";
import { getImperialSession } from "@/lib/api/session";
import { revalidateCmsPageCache } from "@/lib/admin/cache";

/**
 * Server Action — promuje bieżącą konfigurację (layout + okna) do global
 * default templatu w `imp_cms_window_layout_template[is_global_default=1]`.
 *
 * **Idempotentne:** kolejne wywołania nadpisują istniejący template. Frontend
 * (LayoutAndWindowsPanel) pokazuje confirmDialog z odpowiednim komunikatem
 * w zależności od `default_template_exists` z poprzedniego fetch'a metadata.
 *
 * **Auth:** wymaga SSO session (Bearer) + scope `cms:manage_layout` (backend
 * enforce'uje przez `requiresUser: true, scopes: ['cms:manage_layout']`).
 *
 * **Po sukcesie:** `revalidatePath('/', 'layout')` invalidates cache dla
 * WSZYSTKICH stron — bo zmiana default'u potencjalnie wpływa na każdą
 * podstronę która nie ma własnej konfiguracji.
 *
 * @param input
 *   - `layout` — symbol layoutu (`imp_cms_layout.symbol` z `supports_subpage=1`)
 *   - `windowsByArea` — `{ area_key: number[] }` mapa okien do umieszczenia per area
 *   - `lang` — locale ISO ('pl', 'en', 'de') propagowany jako `X-Imperial-Lang`
 */
export async function saveAsDefaultAction(input: {
  layout: string;
  windowsByArea: Record<string, number[]>;
  lang: string;
}): Promise<{
  ok: boolean;
  error?: string;
  templateId?: number;
  windowsInserted?: number;
  wasUpdate?: boolean;
}> {
  const session = await getImperialSession();
  if (!session) {
    return { ok: false, error: "unauthenticated" };
  }

  try {
    const response = await apiClient<{
      data: {
        template_id: number;
        layout: string;
        windows_inserted: number;
      };
      meta: {
        was_update: boolean;
      };
    }>("/IRESTAPI/v1/IWINDOWS_MANAGER_CONTROLLER/saveAsDefault", {
      method: "POST",
      bearerToken: session.accessToken,
      lang: input.lang,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        layout: input.layout,
        windows_by_area: input.windowsByArea,
      }),
    });

    /* Revalidate WSZYSTKICH layoutów — zmiana default'u może wpłynąć na
       każdą podstronę bez własnej konfiguracji. Helper invaliduje globalnie
       (slug=null -> '/') + tagi (cms-windows, cms-layout, cms-layouts,
       window-content) — gwarancja że pageBootstrap dla każdej podstrony
       z fallback'iem na template_default dostanie świeże dane. */
    await revalidateCmsPageCache();

    return {
      ok: true,
      templateId: response.data.template_id,
      windowsInserted: response.data.windows_inserted,
      wasUpdate: response.meta.was_update,
    };
  } catch (e) {
    if (e instanceof ApiClientError) {
      console.warn(`[saveAsDefaultAction] failed: ${e.message}`);
      return { ok: false, error: e.message };
    }
    return { ok: false, error: "unexpected_error" };
  }
}
